import * as stylex from '@stylexjs/stylex'
import { useState } from "react";
import Avatar from "../../components/Avatar";
import Button from "../../components/Button";
import Icon from "../../components/Icon";
import SearchInput from "../../components/SearchInput";
import SegmentedControl from "../../components/SegmentedControl";
import { formatMoney, type Balance, type Overview } from "../../lib/ledger";


const styles = stylex.create({
  s0: {
    display: "flex",
    flexDirection: "column",
    rowGap: "28px",
  },
  s1: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    "@media (min-width: 640px)": {
      flexDirection: "row",
      alignItems: "end",
      justifyContent: "space-between",
    },
  },
  s2: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#5b9164",
  },
  s3: {
    marginTop: "4px",
    fontSize: "clamp(1.75rem,3.2vw,2.25rem)",
    fontWeight: 600,
    letterSpacing: "-.045em",
  },
  s4: {
    marginTop: "8px",
    fontSize: "14px",
    color: "#707b72",
  },
  s5: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    gap: "8px",
    "@media (min-width: 640px)": {
      width: "auto",
      flexDirection: "row",
    },
  },
  s6: {
    position: "relative",
  },
  s7: {
    pointerEvents: "none",
    position: "absolute",
    left: "12px",
    top: "12px",
    height: "16px",
    width: "16px",
    color: "#7a887d",
  },
  s8: {
    height: "40px",
    width: "100%",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#dbe5da",
    backgroundColor: "#fff",
    paddingLeft: "36px",
    paddingRight: "12px",
    fontSize: "13px",
    outline: "none",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    "::placeholder": {
      color: "#99a099",
    },
    ":focus": {
      borderColor: "#5b9a6c",
      boxShadow: "0 0 0 1px color-mix(in srgb, #5b9a6c 10%, transparent)",
    },
    "@media (min-width: 640px)": {
      width: "224px",
    },
  },
  s9: {
    display: "flex",
    height: "40px",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    alignSelf: "start",
    borderRadius: "0.75rem",
    backgroundColor: "#408b56",
    paddingInline: "16px",
    fontSize: "12px",
    fontWeight: 600,
    color: "#fff",
    boxShadow: "0 1px 2px 0 rgb(0 0 0 / 0.05)",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#347c48",
    },
    "@media (min-width: 640px)": {
      alignSelf: "auto",
    },
  },
  s10: {
    height: "16px",
    width: "16px",
  },
  s11: {
    display: "flex",
    width: "100%",
    gap: "4px",
    overflowX: "auto",
    borderRadius: "0.75rem",
    borderWidth: 1,
    borderColor: "#e1e8df",
    backgroundColor: "#f1f6ef",
    padding: "4px",
    "@media (min-width: 640px)": {
      width: "fit-content",
    },
  },
  s12: {
    overflow: "hidden",
    borderRadius: "1rem",
    borderWidth: 1,
    borderColor: "#e1e8df",
    backgroundColor: "#fff",
    boxShadow: "0 4px 16px rgba(48,70,51,.035)",
  },
  s13: {
    display: "grid",
    gridTemplateColumns: "minmax(0,1fr) auto",
    gap: "16px",
    borderBottomWidth: 1,
    borderColor: "#edf0eb",
    paddingInline: "16px",
    paddingBlock: "12px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: ".08em",
    color: "#889188",
    "@media (min-width: 640px)": {
      paddingInline: "20px",
    },
  },
  s14: { borderTopWidth: 1, borderTopColor: "#edf0eb" },
  s15: {
    paddingInline: "20px",
    paddingBlock: "48px",
    textAlign: "center",
    fontSize: "13px",
    color: "#748077",
  },
  s16: {
    display: "grid",
    cursor: "pointer",
    gridTemplateColumns: "minmax(0,1fr) auto",
    alignItems: "center",
    gap: "16px",
    paddingInline: "16px",
    paddingBlock: "16px",
    textAlign: "left",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#f8fbf7",
    },
    "@media (min-width: 640px)": {
      paddingInline: "20px",
    },
  },
  s17: {
    display: "flex",
    minWidth: "0px",
    alignItems: "center",
    gap: "12px",
  },
  s18: {
    minWidth: "0px",
  },
  s19: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    fontSize: "14px",
    fontWeight: 600,
  },
  s20: {
    textAlign: "right",
  },
  s21: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  s22: {
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
    gap: "8px",
  },
  s23: {
    height: "32px",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dce7da",
    paddingInline: "10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#3b7b4d",
    transition: "color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter",
    ":hover": {
      backgroundColor: "#edf5eb",
    },
  },
  s24: {
    display: "inline-flex",
    height: "32px",
    alignItems: "center",
    gap: "4px",
    borderRadius: "0.5rem",
    backgroundColor: "#f3f7f2",
    paddingInline: "10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#647066",
  },
  s25: {
    height: "14px",
    width: "14px",
  },
  shareButton: {
    marginTop: "8px",
    height: "30px",
    borderRadius: "0.5rem",
    borderWidth: 1,
    borderColor: "#dce7da",
    paddingInline: "10px",
    fontSize: "11px",
    fontWeight: 600,
    color: "#526b58",
    ":hover": { backgroundColor: "#f0f6ee" },
  },
})

const peopleStyles = stylex.create({
  filter: { whiteSpace: 'nowrap', borderRadius: '0.5rem', paddingInline: '16px', paddingBlock: '8px', fontSize: '12px', fontWeight: 600, transition: 'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter' },
  filterActive: { backgroundColor: '#fff', color: '#3e7d4d', boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
  filterIdle: { color: '#718073', ':hover': { color: '#3e7d4d' } },
  summary: { marginTop: '2px', fontSize: '12px' },
  summaryMixed: { color: '#68736b' },
  summaryPositive: { color: '#397a4b' },
  summaryNegative: { color: '#a5501d' },
  summaryZero: { color: '#78827a' },
  amount: { fontSize: '14px', fontWeight: 600 },
  amountPositive: { color: '#2e7943' },
  amountNegative: { color: '#a44f1c' },
  kind: { fontSize: '10px', fontWeight: 500 },
  kindPositive: { color: '#5b9164' },
  kindNegative: { color: '#b1673d' },
})
type PeoplePageProps = {
  data: Overview;
  onAddPerson: () => void;
  onSettle: (balance: Balance) => void;
  onShare: (personID: string) => void;
  onOpenPerson?: (personID: string) => void;
};

const currencyOrder = new Map([
  ["AWG", 0],
  ["USD", 1],
  ["EUR", 2],
]);

function balanceSummary(balances: Balance[]) {
  if (balances.length === 0) return "All settled up";
  if (balances.length === 1)
    return balances[0].amountCents > 0 ? "owes you" : "you owe";
  if (balances.every((balance) => balance.amountCents > 0))
    return `owes you in ${balances.length} currencies`;
  if (balances.every((balance) => balance.amountCents < 0))
    return `you owe in ${balances.length} currencies`;
  return `balances in ${balances.length} currencies`;
}

export default function PeoplePage({
  data,
  onAddPerson,
  onSettle,
  onShare,
  onOpenPerson,
}: PeoplePageProps) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<
    "all" | "unsettled" | "settled" | "owes" | "owed"
  >("all");
  const balancesByPerson = new Map<string, Balance[]>();
  for (const balance of data.combinedBalances ?? data.balances) {
    const balances = balancesByPerson.get(balance.personId) ?? [];
    balances.push(balance);
    balancesByPerson.set(balance.personId, balances);
  }
  for (const balances of balancesByPerson.values()) {
    balances.sort(
      (left, right) =>
        (currencyOrder.get(left.currency) ?? 99) -
          (currencyOrder.get(right.currency) ?? 99) ||
        left.currency.localeCompare(right.currency),
    );
  }
  const sortedPeople = [...data.people].sort((left, right) => {
    const leftBalances = balancesByPerson.get(left.id) ?? [];
    const rightBalances = balancesByPerson.get(right.id) ?? [];
    const leftOwed = Math.max(
      0,
      ...leftBalances
        .filter((balance) => balance.amountCents > 0)
        .map((balance) => balance.amountCents),
    );
    const rightOwed = Math.max(
      0,
      ...rightBalances
        .filter((balance) => balance.amountCents > 0)
        .map((balance) => balance.amountCents),
    );
    const leftOwes = Math.max(
      0,
      ...leftBalances
        .filter((balance) => balance.amountCents < 0)
        .map((balance) => Math.abs(balance.amountCents)),
    );
    const rightOwes = Math.max(
      0,
      ...rightBalances
        .filter((balance) => balance.amountCents < 0)
        .map((balance) => Math.abs(balance.amountCents)),
    );
    return (
      rightOwed - leftOwed ||
      rightOwes - leftOwes ||
      left.name.localeCompare(right.name)
    );
  });
  const normalizedQuery = query.trim().toLowerCase();
  const filteredPeople = sortedPeople.filter((person) => {
    const balances = balancesByPerson.get(person.id) ?? [];
    const unsettled = balances.some((balance) => balance.amountCents !== 0);
    const owes = balances.some((balance) => balance.amountCents < 0);
    const owed = balances.some((balance) => balance.amountCents > 0);
    return (
      filter === "all" ||
      (filter === "unsettled"
        ? unsettled
        : filter === "settled"
          ? !unsettled
          : filter === "owes"
            ? owes
            : owed)
    );
  });
  const visiblePeople = normalizedQuery
    ? filteredPeople.filter((person) =>
        person.name.toLowerCase().includes(normalizedQuery),
      )
    : filteredPeople;
  const emptyMessage =
    filter === "unsettled"
      ? "No unsettled people."
      : filter === "settled"
        ? "No settled people."
        : filter === "owes"
          ? "No one you owe."
          : filter === "owed"
            ? "No one owes you."
            : "No people match your search.";

  return (
    <div {...stylex.props(styles.s0)}>
      <header {...stylex.props(styles.s1)}>
        <div>
          <p {...stylex.props(styles.s2)}>Your circle</p>
          <h1 {...stylex.props(styles.s3)}>
            People
          </h1>
          <p {...stylex.props(styles.s4)}>
            See the full balance you share with each person.
          </p>
        </div>
        <div {...stylex.props(styles.s5)}>
          <SearchInput
            aria-label="Search people"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people…"
            value={query}
          />
          <Button onClick={onAddPerson}>
            <Icon name="plus" />
            Add person
          </Button>
        </div>
      </header>
      <SegmentedControl
        onChange={setFilter}
        options={[
          { value: "all", label: "All people" },
          { value: "unsettled", label: "Unsettled" },
          { value: "owes", label: "You owe" },
          { value: "owed", label: "Owed to you" },
          { value: "settled", label: "Settled" },
        ]}
        value={filter}
      />
      <section {...stylex.props(styles.s12)}>
        <div {...stylex.props(styles.s13)}>
          <span>Person</span>
          <span>Balance</span>
        </div>
        <div {...stylex.props(styles.s14)}>
          {visiblePeople.length === 0 ? (
            <p {...stylex.props(styles.s15)}>
              {emptyMessage}
            </p>
          ) : (
            visiblePeople.map((person) => {
              const balances = balancesByPerson.get(person.id) ?? [];
              const hasPositive = balances.some(
                (balance) => balance.amountCents > 0,
              );
              const hasNegative = balances.some(
                (balance) => balance.amountCents < 0,
              );
              const summaryTone =
                hasPositive && hasNegative
                  ? peopleStyles.summaryMixed
                  : hasPositive
                    ? peopleStyles.summaryPositive
                    : hasNegative
                      ? peopleStyles.summaryNegative
                      : peopleStyles.summaryZero;
              return (
                <article
                  {...stylex.props(styles.s16)}
                  key={person.id}
                  onClick={() => onOpenPerson?.(person.id)}
                  role="button"
                  tabIndex={0}
                >
                  <div {...stylex.props(styles.s17)}>
                    <Avatar person={person} size={42} />
                    <div {...stylex.props(styles.s18)}>
                      <h2 {...stylex.props(styles.s19)}>
                        {person.name}
                      </h2>
                      <p {...stylex.props(peopleStyles.summary, summaryTone)}>
                        {balanceSummary(balances)}
                      </p>
                    </div>
                  </div>
                  <div {...stylex.props(styles.s20)}>
                    {balances.length > 0 ? (
                      <div {...stylex.props(styles.s21)}>
                        {balances.map((balance) => {
                          const receives = balance.amountCents > 0;
                          return (
                            <div
                              {...stylex.props(styles.s22)}
                              key={`${balance.personId}-${balance.currency}`}
                            >
                              <div>
                                <p
                                  {...stylex.props(peopleStyles.amount, receives ? peopleStyles.amountPositive : peopleStyles.amountNegative)}
                                >
                                  {formatMoney(
                                    Math.abs(balance.amountCents),
                                    balance.currency,
                                  )}
                                </p>
                                <p
                                  {...stylex.props(peopleStyles.kind, receives ? peopleStyles.kindPositive : peopleStyles.kindNegative)}
                                >
                                  {receives ? "owes you" : "you owe"}
                                </p>
                              </div>
                              <button
                                aria-label={`Record ${balance.currency} payment with ${person.name}`}
                                {...stylex.props(styles.s23)}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  if (data.combinedBalances && onOpenPerson) onOpenPerson(person.id);
                                  else onSettle(balance);
                                }}
                                type="button"
                              >
                                {data.combinedBalances ? 'Details' : 'Record'}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span {...stylex.props(styles.s24)}>
                        <Icon {...stylex.props(styles.s25)} name="check" />
                        Settled
                      </span>
                    )}
                    <button
                      {...stylex.props(styles.shareButton)}
                      onClick={(event) => {
                        event.stopPropagation();
                        onShare(person.id);
                      }}
                      type="button"
                    >
                      Share
                    </button>
                  </div>
                </article>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
